import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useAction } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { Loader2, LogIn, LogOut, MailCheck, UserPlus } from "lucide-react";
import { api } from "@constructionautomation/backend";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { invitationPath } from "@/lib/auth-return-to";
import { enterOrganization } from "@/lib/enter-organization";
import { usePageRestore } from "@/lib/use-page-restore";
import { describeRoleSlug, formatRoleSlug, getSignupErrorMessage } from "@/lib/signup-errors";
import { captureUserActionError } from "@/lib/sentry";

type InvitationDetails = FunctionReturnType<typeof api.signup.describeInvitation>;

interface InvitationScreenProps {
  token: string | null;
  isAuthenticated: boolean;
}

const STATUS_MESSAGES: Record<"expired" | "revoked" | "accepted" | "not_found", { title: string; body: string }> = {
  expired: {
    title: "This invitation has expired",
    body: "Ask your company admin to resend the invitation, then open the new link.",
  },
  revoked: {
    title: "This invitation was revoked",
    body: "Ask your company admin to invite you again if you still need access.",
  },
  accepted: {
    title: "This invitation was already used",
    body: "Sign in with the account that accepted it, or ask your company admin for a new invitation.",
  },
  not_found: {
    title: "This invitation link isn't valid",
    body: "Check that you opened the full link from your invitation email, or ask your company admin to resend it.",
  },
};

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border py-2 last:border-b-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="break-all text-sm font-semibold text-foreground">{value}</dd>
    </div>
  );
}

/**
 * Invitation landing page (`/invite?invitation_token=...`). Shows who invited whom before any
 * account change, and offers recovery for wrong-account, expired, and revoked invitations
 * without steering invitees into creating their own company.
 */
export function InvitationScreen({ token, isAuthenticated }: InvitationScreenProps) {
  const auth = useAuth();
  const { signIn, signUp, signOut } = auth;
  const describeInvitation = useAction(api.signup.describeInvitation);
  const acceptInvitation = useAction(api.signup.acceptInvitation);
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"accept" | "signOut" | "auth" | null>(null);
  const autoContinued = useRef(false);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    // Only the newest request for the current token may update what the user is confirming.
    const request = ++latestRequest.current;
    setLoadError(null);
    if (!token) {
      setDetails({ status: "not_found", organizationName: null, invitedEmail: null, roleSlug: null, expiresAt: null, viewer: null });
      return;
    }
    try {
      const next = await describeInvitation({ token });
      if (request === latestRequest.current) setDetails(next);
    } catch (error) {
      if (request !== latestRequest.current) return;
      captureUserActionError(error, "auth.describeInvitation");
      setLoadError(getSignupErrorMessage(error, "We couldn't load this invitation."));
    }
  }, [describeInvitation, token]);

  useEffect(() => {
    // Never show (or accept from) the previous token's details while a new token loads.
    setDetails(null);
    setActionError(null);
    autoContinued.current = false;
  }, [token]);

  useEffect(() => {
    void load();
  }, [load, isAuthenticated]);

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setPendingAction("accept");
    setActionError(null);
    try {
      const result = await acceptInvitation({ token });
      await enterOrganization(auth, result.workosOrganizationId, "/");
    } catch (error) {
      captureUserActionError(error, "auth.acceptInvitation");
      setActionError(getSignupErrorMessage(error, "We couldn't accept this invitation."));
      setPendingAction(null);
      void load();
    }
  }, [acceptInvitation, auth, load, token]);

  // Accept and hosted auth leave the page with `pendingAction` set; Back restores it from bfcache.
  // A restored member view continues into the company as a fresh load would, instead of spinning.
  usePageRestore(() => {
    setPendingAction(null);
    if (details?.viewer?.isMember) void handleAccept();
  });

  // Hosted sign-up/sign-in with the token usually accepts the invitation already; reconcile and go in.
  useEffect(() => {
    if (details?.viewer?.isMember && !autoContinued.current) {
      autoContinued.current = true;
      void handleAccept();
    }
  }, [details, handleAccept]);

  const handleSignOut = async () => {
    setPendingAction("signOut");
    await signOut({ navigate: false });
    // Reloading keeps this invitation URL, so the invited account can sign in next.
    window.location.reload();
  };

  const startAuth = async (mode: "signIn" | "signUp") => {
    if (!token) return;
    setPendingAction("auth");
    setActionError(null);
    const options = {
      invitationToken: token,
      loginHint: details?.invitedEmail ?? undefined,
      state: { returnTo: invitationPath(token) },
    };
    try {
      await (mode === "signUp" ? signUp(options) : signIn(options));
    } catch (error) {
      // Redirect never started (e.g. storage blocked); let the user retry.
      captureUserActionError(error, "auth.startInvitationAuth");
      setActionError("We couldn't open sign-in. Check that cookies and site storage are allowed, then try again.");
      setPendingAction(null);
    }
  };

  if (!details && !loadError) {
    return (
      <InvitationLayout title="Loading invitation" description="Checking your invitation link.">
        <div className="flex justify-center py-4" role="status" aria-label="Loading invitation">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </InvitationLayout>
    );
  }

  if (loadError || !details) {
    return (
      <InvitationLayout title="Invitation unavailable" description="Something went wrong while loading your invitation.">
        <Alert variant="destructive">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
        <Button className="w-full" onClick={() => void load()}>
          Try Again
        </Button>
      </InvitationLayout>
    );
  }

  const viewer = details.viewer;
  const companyName = details.organizationName ?? "your company";
  const isBusy = pendingAction !== null;
  const signOutButton = isAuthenticated ? (
    <Button className="w-full" variant="outline" onClick={() => void handleSignOut()} disabled={isBusy}>
      {pendingAction === "signOut" ? <Loader2 className="size-4 animate-spin" /> : <LogOut className="size-4" />}
      Sign Out and Switch Account
    </Button>
  ) : null;

  const detailList = details.status !== "not_found" ? (
    <dl className="rounded-lg border-2 border-border bg-muted/30 px-4 py-1">
      <DetailRow label="Company" value={companyName} />
      <DetailRow label="Invited email" value={details.invitedEmail} />
      <DetailRow label="Role" value={formatRoleSlug(details.roleSlug)} />
      {viewer ? <DetailRow label="Signed in as" value={viewer.email} /> : null}
    </dl>
  ) : null;

  const roleDescription = details.status === "pending" ? describeRoleSlug(details.roleSlug) : null;
  const roleNote = roleDescription ? <p className="text-sm text-muted-foreground">{roleDescription}</p> : null;

  const errorAlert = actionError ? (
    <Alert variant="destructive">
      <AlertDescription>{actionError}</AlertDescription>
    </Alert>
  ) : null;

  if (viewer?.isMember) {
    return (
      <InvitationLayout title={`Joining ${companyName}`} description="Finishing your company access.">
        {detailList}
        {errorAlert ?? (
          <div className="flex justify-center py-2" role="status" aria-label="Joining company">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {actionError ? (
          <Button className="w-full" onClick={() => void handleAccept()} disabled={isBusy}>
            Try Again
          </Button>
        ) : null}
      </InvitationLayout>
    );
  }

  if (details.status !== "pending") {
    const message = STATUS_MESSAGES[details.status];
    return (
      <InvitationLayout title={message.title} description={message.body}>
        {detailList}
        {signOutButton}
        {!isAuthenticated && details.status === "accepted" ? (
          <Button className="w-full" onClick={() => void signIn()} disabled={isBusy}>
            <LogIn className="size-4" />
            Sign In
          </Button>
        ) : null}
        {isAuthenticated ? (
          <Button className="w-full" variant="ghost" onClick={() => window.location.assign("/")} disabled={isBusy}>
            Go to Construction Cortex
          </Button>
        ) : null}
      </InvitationLayout>
    );
  }

  const title = `Join ${companyName}`;

  if (!viewer) {
    return (
      <InvitationLayout
        title={title}
        description={`You've been invited to join ${companyName} on Construction Cortex.`}
      >
        {detailList}
        {roleNote}
        {errorAlert}
        <Button className="w-full" onClick={() => void startAuth("signUp")} disabled={isBusy}>
          <UserPlus className="size-4" />
          Create Account to Join
        </Button>
        <Button className="w-full" variant="outline" onClick={() => void startAuth("signIn")} disabled={isBusy}>
          <LogIn className="size-4" />
          I Already Have an Account
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Use {details.invitedEmail} so the invitation can be matched to your account.
        </p>
      </InvitationLayout>
    );
  }

  if (!viewer.emailMatches) {
    return (
      <InvitationLayout title={title} description="This invitation belongs to a different account.">
        {detailList}
        <Alert variant="warning">
          <AlertTitle>Wrong account</AlertTitle>
          <AlertDescription>
            This invitation was sent to {details.invitedEmail}, but you're signed in as {viewer.email}.
            Sign out, then sign in or create an account with {details.invitedEmail}.
          </AlertDescription>
        </Alert>
        {signOutButton}
      </InvitationLayout>
    );
  }

  if (viewer.otherOrganizationName) {
    return (
      <InvitationLayout title={title} description="Your account already belongs to a company.">
        {detailList}
        <Alert variant="warning">
          <AlertTitle>One company per account</AlertTitle>
          <AlertDescription>
            You already belong to {viewer.otherOrganizationName}. An account can belong to one company at a
            time, so this invitation can't be accepted. Ask {companyName}'s admin to invite a different email
            address, or ask your current admin to remove you first.
          </AlertDescription>
        </Alert>
        <Button className="w-full" variant="outline" onClick={() => window.location.assign("/")} disabled={isBusy}>
          Go to {viewer.otherOrganizationName}
        </Button>
        {signOutButton}
      </InvitationLayout>
    );
  }

  return (
    <InvitationLayout title={title} description={`Accept to join ${companyName} as ${formatRoleSlug(details.roleSlug)}.`}>
      {detailList}
      {roleNote}
      {errorAlert}
      <Button className="w-full" onClick={() => void handleAccept()} disabled={isBusy}>
        {pendingAction === "accept" ? <Loader2 className="size-4 animate-spin" /> : <MailCheck className="size-4" />}
        {pendingAction === "accept" ? "Joining..." : `Accept and Join ${companyName}`}
      </Button>
      {signOutButton}
    </InvitationLayout>
  );
}

function InvitationLayout({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md py-4">
        <CardHeader className="pb-2 text-center">
          <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <MailCheck className="size-6" aria-hidden="true" />
          </div>
          <CardTitle className="text-lg tracking-tight">
            <h1>{title}</h1>
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">{children}</CardContent>
      </Card>
    </main>
  );
}
